import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Search, MapPin, Star, TrendingUp, ArrowRight } from 'lucide-react';
import './ResourceGuide.css';

function SEOGuide() {
  return (
    <div className="guide-container" role="main">
      <div className="guide-header-section">
        <Link to="/resources" className="back-link">← Back to Resources</Link>
        <h1>SEO for Your Business</h1>
        <p className="guide-subtitle">Rank higher on Google and attract local customers searching for you</p>
      </div>

      <div className="guide-content">
        <section className="guide-section">
          <h2>What is SEO and Why It Matters</h2>
          <p>
            SEO (Search Engine Optimization) is how you get found on Google. When someone searches for a business like yours, 
            SEO determines if you show up on page 1 or page 10. It's the difference between getting customers and losing them to competitors.
          </p>
          <div className="impact-stats">
            <div className="stat">
              <div className="stat-number">68%</div>
              <p>of online experiences start with a search</p>
            </div>
            <div className="stat">
              <div className="stat-number">76%</div>
              <p>search for "near me" on mobile</p>
            </div>
            <div className="stat">
              <div className="stat-number">88%</div>
              <p>trust online reviews as much as personal recommendations</p>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Local SEO: The Big Win for Local Businesses</h2>
          <p>
            If you serve customers in a specific area (city, neighborhood, region), local SEO is your goldmine. 
            It's easier to rank for "plumber in Austin" than "best plumber in the world."
          </p>
          <div className="step-card">
            <h3>The 3 Pillars of Local SEO:</h3>
            <ul className="feature-list">
              <li><MapPin size={20} /> <strong>Google Business Profile</strong> - Your free listing on Google Maps and search</li>
              <li><Star size={20} /> <strong>Local Citations & Reviews</strong> - Mentions and ratings across the web</li>
              <li><Search size={20} /> <strong>Local Keywords</strong> - Using location words customers actually search</li>
            </ul>
          </div>
        </section>

        <section className="guide-section">
          <h2>Step-by-Step Local SEO Roadmap</h2>
          
          <div className="roadmap">
            <div className="roadmap-item">
              <h3>Week 1: Claim Your Google Business Profile</h3>
              <ol>
                <li>Go to google.com/business</li>
                <li>Search for your business name</li>
                <li>Click "Claim this business"</li>
                <li>Verify your location (phone or mail)</li>
                <li>Fill out EVERY field completely:
                  <ul>
                    <li>Business description (2-3 sentences)</li>
                    <li>Hours of operation</li>
                    <li>Phone number and website</li>
                    <li>Address (or "service area" if you travel to customers)</li>
                    <li>High-quality photos (20+ photos)</li>
                    <li>Business category</li>
                  </ul>
                </li>
              </ol>
            </div>

            <div className="roadmap-item">
              <h3>Week 2-3: Build Citations & Get Reviews</h3>
              <p>Citations are mentions of your business across the web. More citations = higher rankings.</p>
              <ul className="feature-list">
                <li><CheckCircle size={20} /> Ensure your NAP (Name, Address, Phone) is identical everywhere</li>
                <li><CheckCircle size={20} /> Add your business to: Yelp, Apple Maps, Facebook, industry directories</li>
                <li><CheckCircle size={20} /> Ask customers for Google reviews (email, text, in-person)</li>
                <li><CheckCircle size={20} /> Respond to all reviews (positive and negative)</li>
              </ul>
            </div>

            <div className="roadmap-item">
              <h3>Month 2: Optimize Your Website for Keywords</h3>
              <p>Make sure Google understands what your business does and where you serve:</p>
              <ul className="feature-list">
                <li><CheckCircle size={20} /> Use location keywords (e.g., "plumber in Austin", "Austin plumbing services")</li>
                <li><CheckCircle size={20} /> Add service keywords naturally throughout your site</li>
                <li><CheckCircle size={20} /> Create location-specific landing pages if you serve multiple areas</li>
                <li><CheckCircle size={20} /> Make sure your site is mobile-friendly</li>
                <li><CheckCircle size={20} /> Improve page load speed</li>
              </ul>
            </div>

            <div className="roadmap-item">
              <h3>Month 3+: Build Authority & Keep Improving</h3>
              <ul className="feature-list">
                <li><CheckCircle size={20} /> Collect more reviews regularly</li>
                <li><CheckCircle size={20} /> Update your Google profile with posts (promotions, updates)</li>
                <li><CheckCircle size={20} /> Build backlinks (other sites linking to yours)</li>
                <li><CheckCircle size={20} /> Create helpful content that answers customer questions</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Common SEO Mistakes to Avoid</h2>
          <div className="mistakes">
            <div className="mistake-card">
              <h3>❌ Incomplete Google Profile</h3>
              <p>Empty fields = missed opportunities. Fill out EVERYTHING.</p>
            </div>
            <div className="mistake-card">
              <h3>❌ Inconsistent Business Information</h3>
              <p>Different phone numbers or addresses online confuse Google. Be consistent everywhere.</p>
            </div>
            <div className="mistake-card">
              <h3>❌ Ignoring Reviews</h3>
              <p>Reviews matter more than you think. Actively ask for them and respond to them.</p>
            </div>
            <div className="mistake-card">
              <h3>❌ Not Mobile Optimized</h3>
              <p>60%+ of searches are on mobile. If your site isn't mobile-friendly, you're losing customers.</p>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Measure Your Progress</h2>
          <div className="metrics">
            <div className="metric-card">
              <TrendingUp size={24} />
              <h3>Google Rankings</h3>
              <p>Track where you rank for key search terms. Aim for page 1.</p>
            </div>
            <div className="metric-card">
              <Search size={24} />
              <h3>Website Traffic</h3>
              <p>Use Google Analytics to see how much traffic comes from search.</p>
            </div>
            <div className="metric-card">
              <Star size={24} />
              <h3>Review Ratings</h3>
              <p>Aim for 4.5+ stars. Higher ratings = higher click-through rates from search results.</p>
            </div>
          </div>
        </section>

        <section className="guide-cta">
          <h2>Let Us Help</h2>
          <p>Our platform includes SEO tools to make all of this easier. Plus, we give you Google-friendly websites out of the box.</p>
          <Link to="/features" className="guide-cta-btn">See Our SEO Features <ArrowRight size={18} /></Link>
        </section>
      </div>

      <div className="guide-navigation">
        <Link to="/resources/case-studies" className="next-guide">
          Next: Case Studies <ArrowRight size={18} />
        </Link>
      </div>
    </div>
  );
}

export default SEOGuide;
